import React from 'react';

import NumberFlow from '@number-flow/react';
import { capitalize } from 'lodash/fp';

import TokenImg from '@/components/TokenImg/TokenImg';
import { Currency } from '@/constants';
import { useWallet } from '@/hooks';
import useDecomposeToken from '@/hooks/useDecomposeToken';
import { calculatePrecisionNumberStats } from '@/utils';

import HeaderWalletDrawerBalanceCardTokenExplorerLink from './HeaderWalletDrawerBalanceCardTokenExplorerLink';
import HeaderWalletDrawerBalancesInfoBadges from './HeaderWalletDrawerBalancesInfoBadges';

interface HeaderWalletDrawerBalanceCardProps {
  currency: Currency;
  shouldHideSmallBalances?: boolean;
}
const HeaderWalletDrawerBalanceCard: React.FC<HeaderWalletDrawerBalanceCardProps> = ({
  currency,
  shouldHideSmallBalances,
}) => {
  const wallet = useWallet(currency);
  const { token, fullName, type } = useDecomposeToken(currency);

  if (!wallet.isConnected) {
    return null;
  }

  const balanceToken = calculatePrecisionNumberStats(wallet.balanceToken, currency, 8).amountWithPrecision;

  const isHidden = Boolean(shouldHideSmallBalances) && Number(wallet.balanceUsd) < 0.1;
  if (isHidden) return null;

  return (
    <div
      className="text-primary bg-background/60 hover:bg-background/80 flex w-full shrink-0 items-center gap-2
        overflow-hidden rounded-xl p-2.5 text-base sm:px-2.5 md:px-4"
    >
      <div className="flex size-12 flex-col items-center justify-center">
        <TokenImg token={currency} className="size-8" />
      </div>
      <div className="flex w-full flex-col">
        <div className="flex flex-row items-center justify-between font-bold">
          <div className="flex items-center gap-1">
            {token}
            <HeaderWalletDrawerBalanceCardTokenExplorerLink currency={currency} />
          </div>
          <div>
            <NumberFlow value={Number(balanceToken)} format={{ maximumFractionDigits: 8 }} />
          </div>
        </div>
        <div className="text-muted-foreground flex flex-row items-center justify-between text-[13px] leading-tight">
          <div>
            {fullName} - <HeaderWalletDrawerBalancesInfoBadges items={[capitalize(type ?? '')]} />
          </div>
          <div>
            <NumberFlow value={Number(wallet.balanceUsd)} prefix="$ " format={{ minimumFractionDigits: 2 }} />
          </div>
        </div>
      </div>
    </div>
  );
};

export default HeaderWalletDrawerBalanceCard;
